/* IU Storefront Quality Product 1.3.10 — presentation only; compact mobile metadata keeps its full text, no cart writes. */
(() => {
  'use strict';
  // Server-rendered stock messages remain useful without JavaScript. Keep the
  // visible message aligned with the theme's selected variant when it changes.
  if (!customElements.get('iu-product-availability')) {
    class ProductAvailability extends HTMLElement {
      connectedCallback() {
        if (this.onChange) return;
        this.onChange = event => {
          const view = this.closest('.productView');
          const target = event.target;
          if (!view || !(target instanceof Element)) return;
          const picker = target.closest('variant-selects, variant-radios');
          if (picker && view.contains(picker)) {
            this.showVariant(picker.currentVariant?.id);
          } else if (target.matches('[name="id"]') && target.form?.id === `product-form-${this.dataset.productId}`) {
            this.showVariant(target.value);
          }
        };
        document.addEventListener('change', this.onChange);
      }
      disconnectedCallback() {
        document.removeEventListener('change', this.onChange);
        this.onChange = null;
      }
      showVariant(id) {
        const panels = [...this.querySelectorAll('[data-iu-variant]')];
        const selected = panels.find(panel => panel.dataset.iuVariant === String(id));
        const visible = selected || panels.find(panel => panel.dataset.iuVariant === 'unavailable');
        panels.forEach(panel => { panel.hidden = panel !== visible; });
        this.dataset.currentVariant = visible?.dataset.iuVariant || '';
      }
    }
    customElements.define('iu-product-availability', ProductAvailability);
  }
  const gallery = document.querySelector('.productView-image-wrapper');
  if (gallery) {
    const stabilize = () => {
      const badges = [...gallery.querySelectorAll('.iu-curation-badges[data-iu-curation-current-product-badges]')];
      let stable = badges.find(badge => badge.parentElement === gallery);
      for (const badge of badges) {
        if (badge.parentElement === gallery) continue;
        // Leave an inert marker so the existing badge renderer does not re-inject
        // a duplicate into the first slide on the next recommendation refresh.
        const marker = document.createElement('span');
        marker.hidden = true;
        marker.setAttribute('data-iu-curation-current-product-badges', 'true');
        badge.replaceWith(marker);
        if (stable) badge.remove();
        else { gallery.classList.add('iu-stable-badge-gallery'); gallery.append(badge); stable = badge; }
      }
    };
    stabilize();
    new MutationObserver(stabilize).observe(gallery, { childList: true, subtree: true });
  }
  const main = document.getElementById('MainContent');
  if (main) {
    // Match the recommendation rails while preserving the native history loader
    // and the original product cards, photo and purchase handlers.
    const recentRails = new WeakMap();
    const mountRecentRails = () => {
      if (!document.body.classList.contains('template-product')) return;
      for (const block of main.querySelectorAll('[data-iu-recent-carousel]')) {
        const rail = block.querySelector('.iu-recent-rail');
        const prev = block.querySelector('.iu-recent-prev');
        const next = block.querySelector('.iu-recent-next');
        if (!rail || !prev || !next) continue;
        let sync = recentRails.get(rail);
        if (!sync) {
          sync = () => {
            prev.disabled = rail.scrollLeft <= 2;
            next.disabled = rail.scrollLeft >= Math.max(0, rail.scrollWidth - rail.clientWidth - 2);
          };
          recentRails.set(rail, sync);
          const move = direction => rail.scrollBy({
            left: direction * rail.clientWidth * .98,
            behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
          });
          prev.addEventListener('click', () => move(-1));
          next.addEventListener('click', () => move(1));
          rail.addEventListener('scroll', sync, { passive: true });
          if ('ResizeObserver' in window) new ResizeObserver(sync).observe(rail);
          else window.addEventListener('resize', sync, { passive: true });
        }
        for (const product of rail.children) product.setAttribute('role', 'listitem');
        requestAnimationFrame(sync);
      }
    };
    // Only the currency label on product-page recommendation prices. Preserve
    // numeric values, sale markup, API data, and native cart/tracking handlers.
    const unifyPriceLabels = root => {
      if (!document.body.classList.contains('template-product')) return;
      const prices = [...root.querySelectorAll('.ri-card-price')];
      if (root.matches?.('.ri-card-price')) prices.push(root);
      const visit = node => {
        for (const child of node.childNodes) {
          if (child.nodeType === 3) child.textContent = child.textContent.replace(/\bRON\b/g, 'lei');
          else if (child.nodeType === 1) visit(child);
        }
      };
      prices.forEach(visit);
    };
    const relatedCards = '.ri-card, [data-recently-viewed-block] .card';
    const genericBrands = new Set(['premium', 'smart', 'luxury', 'vintage', 'sport', 'alte branduri', 'alte marci']);
    const validBrand = value => {
      const brand = String(value || '').trim();
      const key = brand.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      return genericBrands.has(key) ? '' : brand;
    };
    const relatedBrandSources = new WeakMap();
    const readRelatedBrands = block => {
      if (relatedBrandSources.has(block) || !block.dataset.productId) return;
      const source = { vendors: new Map() };
      relatedBrandSources.set(block, source);
      // The native renderer omits vendor from its HTML. Read its existing public
      // product response once, without replacing the renderer or its handlers.
      const params = new URLSearchParams({ action: 'product', product_id: block.dataset.productId });
      const url = `${window.Shopify?.routes?.root || '/'}apps/recommendations-intelligence?${params}`;
      fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(response => response.ok ? response.json() : null)
        .then(data => {
          if (!data?.ok || !block.isConnected) return;
          for (const product of [...(data.similar || []), ...(data.complete || [])]) {
            const vendor = validBrand(product.vendor);
            if (product.handle && vendor) source.vendors.set(String(product.handle), vendor);
          }
          showRelatedBrands(block);
        })
        .catch(() => {}); // Keep the original cards usable if this optional read fails.
    };
    const showRelatedBrands = root => {
      if (!document.body.classList.contains('template-product')) return;
      const cards = new Set(root.querySelectorAll(relatedCards));
      const parent = root.closest?.(relatedCards);
      if (parent) cards.add(parent);
      for (const card of cards) {
        const block = card.closest('[data-ri-product]');
        const brand = validBrand(card.dataset.iuRelatedBrand || card.closest('[data-iu-related-brand]')?.dataset.iuRelatedBrand || relatedBrandSources.get(block)?.vendors.get(card.dataset.handle));
        if (!brand && block) readRelatedBrands(block);
        if (!brand) continue;
        const rows = [...card.children].filter(child => child.classList.contains('iu-curation-material-row'));
        let row = rows.find(child => child.classList.contains('iu-related-meta')) || rows[0];
        if (!row) {
          row = document.createElement('div');
          row.className = 'iu-curation-material-row';
          const image = card.querySelector('.ri-image-wrap, .card-product');
          if (!image) continue;
          image.insertAdjacentElement('afterend', row);
        }
        row.classList.add('iu-related-meta');
        row.setAttribute('role', 'list');
        row.setAttribute('aria-label', 'Brand și material');
        // The existing badge renderer may finish after the brand row appears.
        // Keep its badge in this row without changing its source or lifecycle.
        for (const duplicate of rows) {
          if (duplicate === row) continue;
          while (duplicate.firstChild) row.appendChild(duplicate.firstChild);
          duplicate.remove();
        }
        if (!row.querySelector('.iu-related-brand')) {
          const label = document.createElement('span');
          label.className = 'iu-related-brand';
          label.setAttribute('role', 'listitem');
          label.textContent = brand;
          label.title = brand;
          row.prepend(label);
        }
      }
    };
    const renameMaterials = root => {
      const label = row => row.setAttribute('aria-label', row.classList.contains('iu-related-meta') ? 'Brand și material' : 'Material');
      if (root.matches?.('.iu-curation-material-row')) label(root);
      root.querySelectorAll('.iu-curation-material-row').forEach(label);
      const badges = [...root.querySelectorAll('.iu-curation-badge--material')];
      if (root.matches?.('.iu-curation-badge--material')) badges.push(root);
      for (const badge of badges) badge.title = badge.textContent.trim();
      showRelatedBrands(root);
      unifyPriceLabels(root);
    };
    renameMaterials(main);
    mountRecentRails();
    new MutationObserver(records => {
      records.forEach(record => record.addedNodes.forEach(node => {
        if (node instanceof Element) renameMaterials(node);
      }));
      mountRecentRails();
    }).observe(main, { childList: true, subtree: true });
  }
  const upgradeFilters = root => {
    for (const heading of root.querySelectorAll('.sidebarBlock-heading.is-clicked, .sidebarBlock-heading:not(.is-clicked)')) {
      if (!heading.closest('collection-filters-form') || heading.closest('.refined-widgets') || heading.dataset.iuKeyboardReady) continue;
      heading.dataset.iuKeyboardReady = '1';
      heading.tabIndex = 0;
      heading.setAttribute('role', 'button');
      const sync = () => heading.setAttribute('aria-expanded', heading.classList.contains('is-clicked') ? 'true' : 'false');
      sync();
      heading.addEventListener('keydown', event => {
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); heading.click(); }
      });
      heading.addEventListener('click', () => requestAnimationFrame(sync));
    }
  };
  upgradeFilters(document);
  const sidebar = document.querySelector('#main-collection-filters');
  if (sidebar) new MutationObserver(() => upgradeFilters(sidebar)).observe(sidebar, { childList: true, subtree: true });
})();
